import {
  LiveVoiceFunctionCalls,
  type LiveVoiceFunctionCall,
} from './live-voice-function-calls';
import { LiveVoiceContextCommands } from './live-voice-context-commands';
import {
  chunkLiveVoiceContext,
  isLiveVoiceFinalEvent,
  LIVE_VOICE_CONNECT_TIMEOUT_MS,
  LIVE_VOICE_FINISH_TIMEOUT_MS,
  liveVoiceEventId,
  liveVoiceRecord,
  parseLiveVoiceAnswer,
  parseLiveVoiceEvent,
  type LiveVoiceContextKind,
  type LiveVoiceEvent,
  type LiveVoiceFailure,
} from './live-voice-protocol';

export type { LiveVoiceFunctionCall } from './live-voice-function-calls';

export type LiveVoiceTransport = {
  channel: {
    readyState: string;
    send: (data: string) => void;
    close: () => void;
  };
  offer: (signal: AbortSignal) => Promise<string>;
  accept: (sdp: string) => Promise<void>;
  close: () => void;
};
export type LiveVoiceTransportEvents = {
  message: (data: unknown) => void;
  failure: () => void;
  connectionChanged: (state: string) => void;
};
export type LiveVoiceCoreOptions = {
  eventId?: () => string;
  createTransport: (events: LiveVoiceTransportEvents) => LiveVoiceTransport;
  muteMedia: (muted: boolean) => void;
  stopMedia: () => void;
  interruptPlayback?: () => void;
  isCurrentCall: () => boolean;
  onEvent: (event: Record<string, unknown>) => void;
  onFailure?: (reason: LiveVoiceFailure) => void;
  executeTool: (call: LiveVoiceFunctionCall) => Promise<string>;
  onTranscriptFlush?: (finalized?: boolean) => Promise<boolean>;
};

type ConnectOptions = { apiKey: string; model: string; url: string };
type State = 'new' | 'connecting' | 'active' | 'finishing' | 'closed';
const MAX_QUEUED_CONTEXT_BYTES = 64 * 1024;

/** A single Live WebRTC session. It never reconnects or retries paid creation. */
export class LiveVoiceSessionCore {
  private state: State = 'new';
  private transport: LiveVoiceTransport | null = null;
  private channel: LiveVoiceTransport['channel'] | null = null;
  private readonly abort = new AbortController();
  private readonly calls: LiveVoiceFunctionCalls;
  private readonly contextCommands = new LiveVoiceContextCommands();
  private expectedModel = '';
  private providerSessionId: string | null = null;
  private muted = false;
  private finalized = false;
  private mediaStopped = false;
  private resolveStarted: (() => void) | null = null;
  private rejectStarted: (() => void) | null = null;
  private finishPromise: Promise<boolean> | null = null;
  private resolveFinished: ((complete: boolean) => void) | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | undefined;
  private finishTimer: ReturnType<typeof setTimeout> | undefined;
  private disconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private queuedBytes = 0;
  private queuedContext: Array<{
    content: string;
    kind: LiveVoiceContextKind;
  }> = [];

  constructor(private readonly options: LiveVoiceCoreOptions) {
    this.calls = new LiveVoiceFunctionCalls({
      executeTool: options.executeTool,
      eventId: options.eventId,
      isActive: () => this.state === 'active' && options.isCurrentCall(),
      send: (event) => this.send(event),
      onFailure: (reason) => this.fail(reason),
    });
  }

  get sessionId(): string | null {
    return this.providerSessionId;
  }

  get isClosing(): boolean {
    return this.state === 'finishing' || this.state === 'closed';
  }

  /** Latest caption position, for diagnostics only. */
  get currentInputTranscriptEndMs(): number | null {
    return this.calls.currentInputTranscriptEndMs;
  }

  getRejectedContextKind(
    event: Record<string, unknown>
  ): LiveVoiceContextKind | null {
    return this.contextCommands.rejectedKind(event);
  }

  async connect(args: ConnectOptions): Promise<void> {
    if (this.state !== 'new' || !this.options.isCurrentCall()) {
      throw new Error('Live voice connection is no longer available');
    }
    this.state = 'connecting';
    this.expectedModel = args.model;
    const started = new Promise<void>((resolve, reject) => {
      this.resolveStarted = resolve;
      this.rejectStarted = () =>
        reject(new Error('Live voice connection failed'));
    });
    this.connectTimer = setTimeout(
      () => this.fail('connection'),
      LIVE_VOICE_CONNECT_TIMEOUT_MS
    );
    try {
      await Promise.all([this.openConnection(args), started]);
      this.assertCurrent();
    } catch {
      this.close();
      throw new Error('Live voice connection failed');
    } finally {
      clearTimeout(this.connectTimer);
    }
  }

  mute(muted: boolean): void {
    if (this.state === 'closed' || this.state === 'finishing') return;
    this.muted = muted;
    this.options.muteMedia(muted);
    if (this.state === 'active') {
      try {
        this.send({
          type: muted
            ? 'session.input_audio.mute'
            : 'session.input_audio.unmute',
          event_id: (this.options.eventId ?? liveVoiceEventId)(),
        });
      } catch {
        this.fail('connection');
      }
    }
  }

  appendContext(content: string, kind: LiveVoiceContextKind): boolean {
    if (
      this.state === 'closed' ||
      this.state === 'finishing' ||
      !this.options.isCurrentCall()
    )
      return false;
    let chunks: string[];
    try {
      chunks = chunkLiveVoiceContext(content);
    } catch {
      this.fail('capacity');
      return false;
    }
    if (this.state !== 'active') {
      const bytes = new TextEncoder().encode(content).byteLength;
      if (this.queuedBytes + bytes > MAX_QUEUED_CONTEXT_BYTES) {
        this.fail('capacity');
        return false;
      }
      this.queuedBytes += bytes;
      this.queuedContext.push(
        ...chunks.map((chunk) => ({ content: chunk, kind }))
      );
      return true;
    }
    try {
      for (const chunk of chunks) this.sendContext(chunk, kind);
      return true;
    } catch {
      this.fail('connection');
      return false;
    }
  }

  interrupt(): void {
    if (this.state !== 'active' || !this.options.isCurrentCall()) return;
    this.options.interruptPlayback?.();
    this.appendContext(
      'Stop the current speech and listen for the user’s next request.',
      'instructions'
    );
  }

  finish(): Promise<boolean> {
    void this.options.onTranscriptFlush?.();
    if (this.finishPromise) return this.finishPromise;
    if (this.state !== 'active') {
      this.close();
      return (
        this.options
          .onTranscriptFlush?.(this.finalized)
          .then((saved) => this.finalized && saved) ??
        Promise.resolve(this.finalized)
      );
    }
    this.state = 'finishing';
    this.calls.stop();
    this.stopMedia();
    this.finishPromise = new Promise((resolve) => {
      this.resolveFinished = resolve;
    });
    this.finishTimer = setTimeout(
      () => this.close(),
      LIVE_VOICE_FINISH_TIMEOUT_MS
    );
    try {
      // The platform's ids: React Native has no `crypto.randomUUID`, and a
      // throw here would drop the connection before the final usage arrives.
      this.send(
        {
          type: 'session.close',
          event_id: (this.options.eventId ?? liveVoiceEventId)(),
        },
        true
      );
    } catch {
      this.close();
    }
    return this.finishPromise;
  }

  close(): void {
    if (this.state === 'closed') return;
    const transcriptUpload = this.options.onTranscriptFlush?.(this.finalized);
    this.state = 'closed';
    this.calls.stop();
    this.abort.abort();
    clearTimeout(this.connectTimer);
    clearTimeout(this.finishTimer);
    clearTimeout(this.disconnectTimer);
    this.rejectStarted?.();
    if (transcriptUpload) {
      void transcriptUpload.then(
        (saved) => this.resolveFinished?.(this.finalized && saved),
        () => this.resolveFinished?.(false)
      );
    } else this.resolveFinished?.(this.finalized);
    this.queuedContext = [];
    this.queuedBytes = 0;
    this.contextCommands.clear();
    this.stopMedia();
    const cleanup = [
      () => this.channel?.close(),
      () => this.transport?.close(),
    ];
    for (const release of cleanup) {
      try {
        release();
      } catch {
        /* Continue releasing independent resources. */
      }
    }
  }

  private stopMedia(): void {
    if (this.mediaStopped) return;
    this.mediaStopped = true;
    this.options.stopMedia();
  }

  private assertCurrent(): void {
    if (this.abort.signal.aborted || !this.options.isCurrentCall()) {
      throw new Error('Live voice connection is no longer available');
    }
  }

  private fail(reason: LiveVoiceFailure): void {
    if (this.state === 'closed') return;
    this.close();
    this.options.onFailure?.(reason);
  }

  private send(event: LiveVoiceEvent, allowFinishing = false): void {
    const allowed =
      this.state === 'active' || (allowFinishing && this.state === 'finishing');
    if (!allowed || this.channel?.readyState !== 'open') {
      throw new Error('Live voice event channel is unavailable');
    }
    if (!allowFinishing) this.assertCurrent();
    this.channel.send(JSON.stringify(event));
  }

  private sendContext(content: string, kind: LiveVoiceContextKind): void {
    const eventId = (this.options.eventId ?? liveVoiceEventId)();
    this.send({
      type: `session.${kind}.append`,
      event_id: eventId,
      delegation_id: null,
      content,
    });
    this.contextCommands.remember(eventId, kind);
  }

  private receive(data: unknown): void {
    if (this.state === 'closed') return;
    // Final accounting must drain even after the UI relinquishes the call.
    if (this.state !== 'finishing' && !this.options.isCurrentCall())
      return this.close();
    const event = parseLiveVoiceEvent(data);
    if (!event) return this.fail('protocol');
    try {
      if (event.type === 'session.started') {
        const session = liveVoiceRecord(event.session);
        if (
          session?.id !== this.providerSessionId ||
          session?.model !== this.expectedModel
        ) {
          return this.fail('protocol');
        }
        if (this.state === 'connecting') {
          this.state = 'active';
          if (this.muted) this.mute(true);
          for (const item of this.queuedContext)
            this.sendContext(item.content, item.kind);
          this.queuedContext = [];
          this.queuedBytes = 0;
          this.resolveStarted?.();
        }
      }
      this.calls.observe(event);
      if (event.type === 'session.closed') {
        if (!isLiveVoiceFinalEvent(event, this.providerSessionId))
          return this.fail('protocol');
        this.finalized = true;
      }
      this.options.onEvent(event);
      if (event.type === 'session.closed') this.close();
    } catch {
      this.fail('protocol');
    }
  }

  private async openConnection(args: ConnectOptions): Promise<void> {
    this.options.muteMedia(this.muted);
    const transport = this.options.createTransport({
      message: (data) => this.receive(data),
      failure: () => this.fail('connection'),
      connectionChanged: (state) => this.connectionChanged(state),
    });
    this.transport = transport;
    this.channel = transport.channel;
    const sdp = await transport.offer(this.abort.signal);
    this.assertCurrent();
    if (!sdp) throw new Error('Live voice SDP offer is unavailable');
    const response = await fetch(args.url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.apiKey}`,
        'Content-Type': 'application/sdp',
      },
      body: sdp,
      signal: this.abort.signal,
    });
    this.assertCurrent();
    if (!response.ok) throw new Error('Live voice negotiation failed');
    const answer = parseLiveVoiceAnswer(await response.json());
    this.assertCurrent();
    if (!answer) throw new Error('Live voice SDP answer is invalid');
    this.providerSessionId = answer.sessionId;
    await transport.accept(answer.sdp);
    this.assertCurrent();
  }

  private connectionChanged(state: string): void {
    if (this.state === 'closed') return;
    clearTimeout(this.disconnectTimer);
    if (state === 'failed' || state === 'closed') {
      this.fail('connection');
    } else if (state === 'disconnected') {
      this.disconnectTimer = setTimeout(() => this.fail('connection'), 5_000);
    }
  }
}
